import { PanelRightClose, PanelRightOpen, Plus } from "@tamagui/lucide-icons";
import { usePathname } from "expo-router";
import { useContext } from "react";
import Animated, { useAnimatedStyle } from "react-native-reanimated";

import Files from "@/assets/icons/Files";
import Orders from "@/assets/icons/Orders";
import {
  Circle,
  Nav,
  Spacer,
  View,
  XStack,
  YStack,
  getTokenValue,
  useTheme,
} from "@/components";
import { Button } from "@/components/Button";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { LayoutContext } from "@/components/LayoutProvider";
import Logo from "@/components/Logo";
import { Text } from "@/components/Typography";
import { VisitStatusIndicatorSidebar } from "@/components/VisitStatusIndicator";
import History from "@/conversation/History";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import SettingsMenu from "@/settings/SettingsMenu";

const SIDEBAR_PANEL_ID = "sidebar-panel";

export default function Sidebar({ visible }: { visible: boolean }) {
  const { lg, anims, sidebarState, toggleSidebar } = useContext(LayoutContext);
  const theme = useTheme();
  const router = usePropagatedRouter();
  const expanded = sidebarState !== "collapsed";
  const { value: ordersFeedEnabled } = useFeatureFlag("orders-feed", false);
  const pathname = usePathname();

  const isSidebarOpen = ["expanding", "expanded"].includes(sidebarState);
  const ToggleIcon = isSidebarOpen ? PanelRightOpen : PanelRightClose;
  // Fade out when closing only
  const contentFade = ["collapsing", "collapsed"].includes(sidebarState)
    ? {
        transition: "standard" as const,
        opacity: 0,
      }
    : { transition: null, opacity: 1 };

  const sidebarExpandedWidth = getTokenValue("$sidebarExpandedWidth");
  const sidebarAnimatedStyle = useAnimatedStyle(() => {
    if (lg) {
      return {
        width: sidebarExpandedWidth,
        marginRight: anims.sidebarWidth.value - sidebarExpandedWidth,
        backgroundColor: theme.backgroundGround?.get(),
        transform: [{ translateX: 0 }],
      };
    }
    // Mobile uses translate to avoid layout shifts + redraws
    return {
      transform: [
        { translateX: anims.sidebarWidth.value - sidebarExpandedWidth },
      ],
      backgroundColor: theme.backgroundGround?.get(),
    };
  }, [
    lg,
    theme.backgroundGround.get(),
    sidebarExpandedWidth,
    anims.sidebarWidth,
  ]);

  if (!visible) return null;

  const screenPadding = getTokenValue("$screenPadding");
  const activeNavItemLeftPadding = screenPadding / 2;

  const sidebarPaddingStyles = {
    paddingTop: screenPadding,
    paddingBottom: screenPadding,
    paddingLeft: activeNavItemLeftPadding, // rightPadding handled by _layout
  };

  return (
    <>
      {/* Mobile: toggle outside the sliding sidebar so no counter-animation needed */}
      {!lg && (
        <Button.Wrapper
          position="absolute"
          zIndex={10}
          top="$screenPadding"
          left="$screenPadding"
          aria-label="Toggle sidebar"
          aria-expanded={isSidebarOpen}
          aria-controls={SIDEBAR_PANEL_ID}
          onPress={toggleSidebar}
        >
          <ToggleIcon flexShrink={0} color="$textSecondary" />
        </Button.Wrapper>
      )}
      <Animated.View
        id={SIDEBAR_PANEL_ID}
        style={[
          {
            justifyContent: "space-between",
            overflow: "visible",
            zIndex: lg ? undefined : 2,
            ...sidebarPaddingStyles,
            ...(!lg && {
              position: "absolute",
              width: sidebarExpandedWidth,
              height: "100%",
            }),
          },
          sidebarAnimatedStyle,
        ]}
      >
        <YStack flex={1} minHeight={0}>
          <Button.Wrapper
            aria-label="Toggle sidebar"
            aria-expanded={isSidebarOpen}
            aria-controls={SIDEBAR_PANEL_ID}
            onPress={toggleSidebar}
            gap="$100"
            justifyContent="flex-start"
            alignItems="center"
            alignSelf="flex-start"
            paddingLeft={activeNavItemLeftPadding}
          >
            {lg ? (
              <ToggleIcon flexShrink={0} color="$textSecondary" />
            ) : (
              <Spacer size="$300" />
            )}
            {expanded && (
              <View {...contentFade}>
                <Logo.TextOnly color="$textPrimary" />
              </View>
            )}
          </Button.Wrapper>

          <Nav aria-label="Primary navigation" marginVertical="$500" gap="$100">
            <Button.Wrapper
              onPress={() => {
                if (!lg && expanded) toggleSidebar();
                router.navigate("/");
              }}
              padding="$100"
              dataTrackingId="new-chat"
            >
              <XStack alignItems="center" gap="$100" cursor="pointer">
                <Circle background="$fillPrimary" padding={3}>
                  <Plus color="$backgroundFill" size="$250" />
                </Circle>

                {expanded && (
                  <Text
                    color="$fillPrimary"
                    size="$md"
                    semibold
                    {...contentFade}
                  >
                    New chat
                  </Text>
                )}
              </XStack>
            </Button.Wrapper>
            <Button.Wrapper
              onPress={() => {
                if (!lg && expanded) toggleSidebar();
                router.navigate("/files");
              }}
              alignSelf={expanded ? "stretch" : "flex-start"}
              borderRadius="$150"
              padding="$100"
              backgroundColor={
                pathname.startsWith("/files") ? "$navSelected" : "transparent"
              }
              aria-current={pathname.startsWith("/files") ? "page" : undefined}
              hoverStyle={{ backgroundColor: "$navHover" }}
            >
              <XStack alignItems="center" gap="$100" cursor="pointer">
                <Files color={theme.textPrimary.get()} width={24} height={24} />

                {expanded && (
                  <Text
                    color="$textPrimary"
                    size="$md"
                    semibold
                    {...contentFade}
                  >
                    Files
                  </Text>
                )}
              </XStack>
            </Button.Wrapper>
            {ordersFeedEnabled && (
              <Button.Wrapper
                onPress={() => {
                  if (!lg && expanded) toggleSidebar();
                  router.navigate("/orders");
                }}
                alignSelf={expanded ? "stretch" : "flex-start"}
                borderRadius="$150"
                padding="$100"
                backgroundColor={
                  pathname.startsWith("/orders")
                    ? "$navSelected"
                    : "transparent"
                }
                aria-current={
                  pathname.startsWith("/orders") ? "page" : undefined
                }
                hoverStyle={{ backgroundColor: "$navHover" }}
              >
                <XStack alignItems="center" gap="$100" cursor="pointer">
                  <Orders
                    color={theme.textPrimary.get()}
                    width={24}
                    height={24}
                  />

                  {expanded && (
                    <Text
                      color="$textPrimary"
                      size="$md"
                      semibold
                      {...contentFade}
                    >
                      Orders
                    </Text>
                  )}
                </XStack>
              </Button.Wrapper>
            )}
          </Nav>

          <Nav
            aria-label="Conversation history"
            aria-hidden={!expanded}
            flex={1}
            minHeight={0}
            {...contentFade}
          >
            {expanded && <VisitStatusIndicatorSidebar />}
            {expanded && <History />}
          </Nav>
        </YStack>
        <SettingsMenu collapsed={!expanded} />
      </Animated.View>
    </>
  );
}
